<script setup>
    import __ from '@components/../mixins/locale'
    import axios from 'axios'
    import Comment from '@components/Comment.vue'
    import InputField from '@components/InputField.vue'
    import PhotoSwipe from 'photoswipe'
    import 'photoswipe/style.css'
    import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

    const props = defineProps({
        items: {
            type: Array,
            required: true
        },
        user: {
            type: Object,
            required: false
        },
        profile: {
            type: Object,
            required: false
        },
        userRoute: {
            type: String,
            required: false
        },
        previousPageUrl: {
            type: String,
            required: false
        },
        nextPageUrl: {
            type: String,
            required: false
        },
        perPage: {
            type: Number,
            required: false
        },
        uploader: {
            type: Boolean,
            required: false,
            default () {
                return false
            }
        },
        type: {
            type: Number,
            required: false
        },
        excludeFromGallery: {
            type: Boolean,
            required: false,
            default () {
                return false
            }
        },
        formErrors: {
            type: Object,
            required: false
        }
    });

    const emit = defineEmits(['uploaded']);

    const galleryItems = ref([]);

    const avatarPhotoId = ref(null);

    const form = ref({});

    const uploading = ref(false);

    const loading = ref(false);

    const errors = ref(null);

    const thumbnails = ref([]);

    // PhotoSwipe instance while the lightbox is open, its root element (target for the
    // overlay Teleport) and the index of the slide being shown.
    let lightbox = null;
    const lightboxEl = ref(null);
    const currentIndex = ref(null);

    // The lightbox uses 69% of the screen; the comments panel takes the rest
    // (to the right on wide screens, below on narrow ones).
    const WIDE_SCREEN = 1000;
    const LIGHTBOX_SHARE = 0.69;

    const isAdmin = computed(() => props.user && props.user.user_id == 80 && props.user.username == 'admin');

    const currentItem = computed(() => currentIndex.value === null ? null : galleryItems.value[currentIndex.value]);

    const currentPhotoId = computed(() => currentItem.value ? currentItem.value.id : null);

    const galleryOwner = computed(() => {
        if (props.profile && props.user) {
            if (props.profile.id === props.user.user_id) {
                return true;
            }
        } else if (props.uploader) {
            return true;
        }
        return false;
    });

    // Guests only get the thumbnail; logged in users get the original.
    const fullImage = (galleryItem) => props.user ? galleryItem.original : galleryItem.thumbnail;

    // Width / height of the thumbnail; drives the tile's width and growth in the justified grid.
    const aspectRatio = (galleryItem) => {
        const { width, height } = galleryItem.thumbnail || {};
        return width > 0 && height > 0 ? width / height : 1;
    }

    const username = (galleryItem) => galleryItem.user?.username || '';

    const avatarSrc = (galleryItem) => {
        const src = galleryItem.user?.avatar?.thumbnail?.src;
        return src ? '/storage' + src : null;
    }

    // Guests get no photos at all (the server sends none), just blurred blocks under an
    // 18+ prompt.
    const locked = computed(() => !props.user && !props.uploader);

    // Shapes for the blurred blocks, enough to fill the gate's tall window.
    const PLACEHOLDER_RATIOS = [1.5, .75, 1, 1.33, .8, 1.6, 1, .7];
    const PLACEHOLDER_COUNT = 24;
    const placeholderRatios = Array.from({ length: PLACEHOLDER_COUNT }, (_, i) => PLACEHOLDER_RATIOS[i % PLACEHOLDER_RATIOS.length]);

    // A random colour scene (1-6, see .adult-blur in app.css) per block, never the same as
    // the block before it.
    const placeholderScenes = computed(() => {
        const scenes = [];
        for (let i = 0; i < placeholderRatios.length; i++) {
            let scene;
            do {
                scene = 1 + Math.floor(Math.random() * 6);
            } while (scene === scenes[i - 1]);
            scenes.push(scene);
        }
        return scenes;
    });

    const uploadPhoto = () => {
        let formData = new FormData();
        let photos = form.value.photo;
        for (let photo of photos) {
            formData.append('photo[]', photo);
        }
        if (form.value.description) {
            formData.append('description', form.value.description);
        }
        if (props.excludeFromGallery) {
            formData.append('exclude_from_gallery', 1);
        }
        if (props.type) {
            formData.append('type', props.type);
        }
        formData.append('user_id', props.user.user_id);
        formData.append('api_token', props.user.api_token);
        axios.post(`/api/photo`, formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then(response => {
            uploading.value = true;
            response.data.forEach((photo) => {
                galleryItems.value.push(photo);
            });
            emit('uploaded', galleryItems.value);
            form.value = {};
        }).then(() => {
            uploading.value = false;
        }).catch(caughtErrors => {
            errors.value = caughtErrors.response.data.errors;
        });
    }

    const deletePhoto = () => {
        const galleryItemId = currentIndex.value;
        const photoId = galleryItems.value[galleryItemId].id;
        axios.delete('/api/photo/' + photoId + '?api_token=' + props.user.api_token)
            .then(response => {
                galleryItems.value.splice(galleryItemId, 1);
                lightbox?.close();
                loading.value = false;
            })
            .catch(caughtErrors => {
                errors.value = caughtErrors.response.data.errors;
            });
    }

    const useAsAvatar = () => {
        const galleryItemId = currentIndex.value;
        const photoId = galleryItems.value[galleryItemId].id;
        axios.patch('/api/photo/' + photoId + '?api_token=' + props.user.api_token, {
            type: 'avatar',
            user_id: galleryItems.value[galleryItemId].user_id
            })
            .then(response => {
                avatarPhotoId.value = photoId;
                loading.value = false;
            })
            .catch(caughtErrors => {
                errors.value = caughtErrors.response.data.errors;
            });
    }

    // Shareable links use the PhotoSwipe 4 format: #&gid=1&pid=<1-based index>.
    const parseHash = () => {
        const params = {};
        for (const pair of window.location.hash.substring(1).split('&')) {
            const [key, value] = pair.split('=');
            if (key && value !== undefined) {
                params[key] = parseInt(value, 10);
            }
        }
        return params;
    }

    const setHash = (index) => {
        const url = window.location.pathname + window.location.search + (index === null ? '' : '#&gid=1&pid=' + (index + 1));
        window.history.replaceState(window.history.state, '', url);
    }

    const isTypingTarget = (target) => target instanceof HTMLElement
        && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

    const openLightbox = (index, animate = true) => {
        if (locked.value || !galleryItems.value[index]) {
            return;
        }

        const dataSource = galleryItems.value.map((galleryItem, i) => {
            const image = fullImage(galleryItem);
            return {
                src: '/storage' + image.src,
                width: image.width,
                height: image.height,
                msrc: '/storage' + galleryItem.thumbnail.src,
                element: thumbnails.value[i],
                // Grid tiles crop their thumbnails (object-fit: cover).
                thumbCropped: true,
            };
        });

        lightbox = new PhotoSwipe({
            dataSource,
            index,
            showHideAnimationType: animate ? 'zoom' : 'none',
            bgOpacity: 1,
            // The comments panel sits outside the lightbox, so focus must be allowed to leave it.
            trapFocus: false,
            returnFocus: false,
            getViewportSizeFn: () => window.innerWidth >= WIDE_SCREEN
                ? { x: window.innerWidth * LIGHTBOX_SHARE, y: window.innerHeight }
                : { x: window.innerWidth, y: window.innerHeight * LIGHTBOX_SHARE },
        });

        let previousIndex = index;

        lightbox.on('uiRegister', () => {
            lightbox.ui.registerElement({
                name: 'fullscreen',
                ariaLabel: 'Toggle fullscreen',
                title: 'Toggle fullscreen',
                order: 9,
                isButton: true,
                html: '<svg aria-hidden="true" class="pswp__icn" viewBox="0 0 32 32" width="32" height="32"><path d="M8 13V8h5M19 8h5v5M24 19v5h-5M13 24H8v-5" fill="none" stroke="currentColor" stroke-width="2"/></svg>',
                onClick: () => document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.(),
            });
        });

        lightbox.on('afterInit', () => {
            lightboxEl.value = lightbox.element;
        });

        // Don't let PhotoSwipe's shortcuts (arrows, z, Esc) fire while typing a comment
        // or while a modal (e.g. the login modal) is open over the lightbox.
        lightbox.on('keydown', (e) => {
            const target = e.originalEvent.target;
            if (isTypingTarget(target) || (target instanceof Element && target.closest('[aria-modal="true"]'))) {
                e.preventDefault();
            }
        });

        lightbox.on('change', () => {
            const newIndex = lightbox.currIndex;
            const lastIndex = lightbox.getNumItems() - 1;

            // Moving past either end of a paginated gallery loads the neighbouring page.
            if (props.nextPageUrl && previousIndex === lastIndex && newIndex === 0) {
                window.location.href = props.nextPageUrl;
                return;
            }
            if (props.previousPageUrl && previousIndex === 0 && newIndex === lastIndex) {
                window.location.href = props.previousPageUrl;
                return;
            }

            previousIndex = newIndex;
            currentIndex.value = newIndex;
            setHash(newIndex);
        });

        lightbox.on('close', () => {
            setHash(null);
        });

        lightbox.on('destroy', () => {
            lightbox = null;
            lightboxEl.value = null;
            currentIndex.value = null;
        });

        currentIndex.value = index;
        setHash(index);
        lightbox.init();
    }

    onMounted(() => {
        galleryItems.value = props.items;

        if (props.user && props.user.avatar_photo_id) {
            avatarPhotoId.value = props.user.avatar_photo_id;
        }

        const hash = parseHash();
        if (hash.pid && hash.gid) {
            openLightbox(hash.pid - 1, false);
        }
    });

    onBeforeUnmount(() => {
        lightbox?.destroy();
    });

    watch(() => props.formErrors, (val) => {
        errors.value = val;
    });

    watch(() => props.items, (val) => {
        galleryItems.value = val;
    },
  { deep: true });
</script>

<template>
    <div>
        <div v-if="galleryOwner && !uploading" class="card mb-6"><div class="card-body">
            <h3 class="text-[1.25rem] mb-0">{{ __('Upload photos') }}</h3>
            <div class="flex flex-wrap -mx-gutter">
                <InputField input-type="file" name="photo" :label="__('Photo')" :errors="errors" placeholder="" @update:field="form.photo = $event" :data="form.photo" />
                <InputField v-if="form.photo && form.photo.length == 1" input-type="text" name="description" :label="__('Description')" :disable-label="true" :errors="errors" :placeholder="__('Description')" @update:field="form.description = $event" :data="form.description" />
            </div>
            <div class="flex flex-wrap -mx-gutter" v-if="form.photo">
                <div class="relative w-full px-gutter flex-1">
                    <button type="button" class="btn btn-primary btn-md" @click="uploadPhoto">{{ __('Upload') }}</button>
                </div>
            </div>
        </div></div>

        <!-- On a profile the gallery starts further down the page, so the card sits near the top. -->
        <div v-if="locked" class="adult-gate" :class="{ 'adult-gate--top': profile }">
            <div class="adult-gate__grid" aria-hidden="true">
                <div v-for="(ratio, index) in placeholderRatios" :key="index" class="adult-gate__tile adult-blur" :class="'adult-blur--' + placeholderScenes[index]" :style="{ '--ratio': ratio }"><i></i></div>
            </div>
            <div class="adult-gate__overlay">
                <div class="adult-gate__card">
                    <span class="adult-gate__badge"><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>18+</span>
                    <h3 class="adult-gate__title">{{ __('18+ content') }}</h3>
                    <p class="adult-gate__text">{{ __('This gallery contains adult content. Log in or register to view the photos.') }}</p>
                    <div class="adult-gate__actions"><a href="/login" class="btn btn-secondary" role="button">{{ __('Login') }}</a><a href="/register" class="btn btn-primary" role="button">{{ __('Register') }}</a></div>
                </div>
            </div>
        </div>

        <!-- Justified rows: each tile grows from its thumbnail's aspect ratio so rows fill the width. -->
        <div v-else class="photo-grid-wrap">
            <div class="photo-grid" itemscope itemtype="http://schema.org/ImageGallery">
                <figure v-for="(galleryItem, index) in galleryItems" :key="galleryItem.id" class="photo-tile" :style="{ '--ratio': aspectRatio(galleryItem) }" itemprop="associatedMedia" itemscope itemtype="http://schema.org/ImageObject">
                    <i class="photo-tile__spacer"></i>
                    <a :href="'/storage' + fullImage(galleryItem).src" class="photo-tile__link" itemprop="contentUrl" @click.prevent="openLightbox(index)">
                        <img :ref="el => thumbnails[index] = el" :src="'/storage' + (galleryItem.thumbnail.src)" itemprop="thumbnail" :alt="galleryItem.description || ''" loading="lazy" />
                    </a>
                    <span v-if="galleryItem.comments_count > 0" class="photo-tile__comments" :title="__('Comments')">
                        <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="comments" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="currentColor" d="M416 192c0-88.4-93.1-160-208-160S0 103.6 0 192c0 34.3 14.1 65.9 38 92-13.4 30.2-35.5 54.2-35.8 54.5-2.2 2.3-2.8 5.7-1.5 8.7S4.8 352 8 352c36.6 0 66.9-12.3 88.7-25 32.2 15.7 70.3 25 111.3 25 114.9 0 208-71.6 208-160zm122 220c23.9-26 38-57.7 38-92 0-66.9-53.5-124.2-129.3-148.1.9 6.6 1.3 13.3 1.3 20.1 0 105.9-107.7 192-240 192-10.8 0-21.3-.8-31.7-1.9C207.8 439.6 281.8 480 368 480c41 0 79.1-9.2 111.3-25 21.8 12.7 52.1 25 88.7 25 3.2 0 6.1-1.9 7.3-4.8 1.3-2.9.7-6.3-1.5-8.7-.3-.3-22.4-24.2-35.8-54.5z"></path></svg>
                        {{ galleryItem.comments_count }}
                    </span>
                    <figcaption v-if="username(galleryItem) || galleryItem.description" class="photo-tile__caption" itemprop="caption description">
                        <a v-if="username(galleryItem) && userRoute" :href="'/' + userRoute + '/' + username(galleryItem)">{{ username(galleryItem) }}</a>
                        <span v-if="galleryItem.description" class="photo-tile__description">{{ galleryItem.description }}</span>
                    </figcaption>
                </figure>
            </div>
        </div>

        <!-- Uploader, actions and comments for the current photo, beside (wide screens) or below
             (narrow screens) the lightbox, on a frosted backdrop made from the photo itself. -->
        <Teleport to="body">
            <aside v-if="lightboxEl && currentItem" class="photo-panel">
                <div class="photo-panel__backdrop" :style="{ backgroundImage: `url('/storage${currentItem.thumbnail.src}')` }"></div>
                <div class="photo-panel__body">
                    <header class="photo-panel__header">
                        <img v-if="avatarSrc(currentItem)" :src="avatarSrc(currentItem)" class="photo-panel__avatar" alt="" />
                        <span v-else-if="username(currentItem)" class="photo-panel__avatar photo-panel__avatar--initial">{{ username(currentItem).charAt(0) }}</span>
                        <div class="min-w-0 flex-1">
                            <template v-if="username(currentItem)">
                                <a v-if="userRoute" :href="'/' + userRoute + '/' + username(currentItem)" class="photo-panel__username">{{ username(currentItem) }}</a>
                                <span v-else class="photo-panel__username">{{ username(currentItem) }}</span>
                            </template>
                            <p v-if="currentItem.description" class="photo-panel__description">{{ currentItem.description }}</p>
                        </div>
                    </header>
                    <div v-if="galleryOwner || isAdmin" class="photo-panel__actions">
                        <button type="button" class="btn btn-secondary btn-sm" v-if="currentPhotoId != avatarPhotoId" @click="useAsAvatar">
                            {{ __('UseasAvatar') }}
                        </button>
                        <button type="button" class="btn btn-danger btn-sm" @click="deletePhoto">
                            <svg style="width: 14px; height: 14px;" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="trash-alt" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M32 464a48 48 0 0 0 48 48h288a48 48 0 0 0 48-48V128H32zm272-256a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zm-96 0a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zm-96 0a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zM432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16z"></path></svg>
                        </button>
                    </div>
                    <Comment :flat="true" :is-photo-comment="true" :contextual-id="currentPhotoId" :type="3" :user-id="user ? user.user_id : null"></Comment>
                </div>
            </aside>
        </Teleport>
    </div>
</template>

<style>
.pswp__button--fullscreen path {
    stroke: var(--pswp-icon-color);
}
.pswp {
    --pswp-bg: var(--color-ink);
}
/* Justified grid: a tile's base width is the row height times its aspect ratio, and it
   grows in proportion to that ratio, so every row keeps (roughly) one height and fills
   the width. The ::after filler stops the last row from stretching. */
.photo-grid-wrap {
    position: relative;
}
/* Rows no taller than the thumbnails (scaled to fit 150×150 on upload), so they aren't
   blown up. */
.photo-grid {
    --row-height: 150px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    &::after {
        content: '';
        flex-grow: 999999;
    }
}
.photo-tile {
    position: relative;
    width: calc(var(--row-height) * var(--ratio));
    flex-grow: var(--ratio);
    margin: 0;
    overflow: hidden;
    border-radius: 10px;
    background-color: var(--color-surface-2);
    box-shadow: inset 0 0 0 1px var(--color-line);
    transition: box-shadow .2s ease-out;
    &:hover, &:focus-within {
        box-shadow: inset 0 0 0 1px var(--color-gold), 0 10px 30px -12px rgb(225 29 72 / .6);
    }
}
.photo-tile__spacer {
    display: block;
    padding-bottom: calc(100% / var(--ratio));
}
.photo-tile__link {
    position: absolute;
    inset: 0;
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform .4s ease-out;
    }
}
.photo-tile:hover .photo-tile__link img {
    transform: scale(1.05);
}
.photo-tile__comments {
    position: absolute;
    top: 6px;
    right: 6px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgb(11 7 9 / .55);
    backdrop-filter: blur(6px);
    color: #fff;
    font-size: 12px;
    pointer-events: none;
    svg {
        width: 12px;
    }
}
.photo-tile__caption {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    padding: 28px 10px 8px;
    background: linear-gradient(to top, rgb(11 7 9 / .85), transparent);
    color: var(--color-body);
    font-size: 13px;
    line-height: 1.35;
    pointer-events: none;
    opacity: 0;
    transform: translateY(6px);
    transition: opacity .2s ease-out, transform .2s ease-out;
    a {
        pointer-events: auto;
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
.photo-tile__description {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--color-muted);
}
.photo-tile:hover .photo-tile__caption,
.photo-tile:focus-within .photo-tile__caption {
    opacity: 1;
    transform: none;
}
/* No hover on touch screens, so captions are always shown there. */
@media (hover: none) {
    .photo-tile__caption {
        opacity: 1;
        transform: none;
    }
}
@media (max-width: 767px) {
    .photo-grid {
        --row-height: 120px;
    }
}
@media (max-width: 575px) {
    .photo-grid {
        --row-height: 95px;
        gap: 4px;
    }
    .photo-tile__caption {
        display: none;
    }
}
/* Lightbox side panel. */
.photo-panel {
    position: fixed;
    z-index: 100001;
    overflow: hidden;
    background-color: var(--color-ink);
    border-left: 1px solid var(--color-line);
}
.photo-panel__backdrop {
    position: absolute;
    inset: -60px;
    background-size: cover;
    background-position: center;
    filter: blur(40px) saturate(1.4);
    opacity: .55;
    transition: background-image .3s ease-out;
    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(to bottom, rgb(11 7 9 / .55), rgb(11 7 9 / .85));
    }
}
.photo-panel__body {
    position: relative;
    height: 100%;
    overflow-y: auto;
    padding: 20px;
}
.photo-panel__header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--color-line);
}
.photo-panel__avatar {
    flex: none;
    width: 48px;
    height: 48px;
    border-radius: 999px;
    object-fit: cover;
    box-shadow: 0 0 0 2px var(--color-gold);
}
.photo-panel__avatar--initial {
    display: flex;
    align-items: center;
    justify-content: center;
    background-image: linear-gradient(135deg, var(--color-crimson), var(--color-orchid));
    color: #fff;
    font-weight: 700;
    text-transform: uppercase;
}
.photo-panel__username {
    display: block;
    font-weight: 600;
    font-size: 1.05rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.photo-panel__description {
    margin: 2px 0 0;
    color: var(--color-muted);
    font-size: .9rem;
}
.photo-panel__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding-top: 16px;
}
@media (max-width: 999px) {
    .pswp {
        height: 69%;
    }
    .photo-panel {
        left: 0;
        bottom: 0;
        width: 100%;
        height: 31%;
        border-left: 0;
        border-top: 1px solid var(--color-line);
    }
    .photo-panel__body {
        padding: 12px 16px;
    }
    .photo-panel__avatar {
        width: 36px;
        height: 36px;
    }
}
@media (min-width: 1000px) {
    .pswp {
        width: 69%;
    }
    .photo-panel {
        top: 0;
        right: 0;
        width: 31%;
        height: 100%;
    }
}
</style>
